/**
 * Comment-data access layer — the only place that calls the `comments.*`
 * oRPC endpoints. Every renderer or aggregation widget reads through one of
 * the hooks here (`useCommentTree`, `useCommentStats`, `useMyTopLevelComment`,
 * `useCommentByKey`, `useCommentWithAncestors`, plus the per-viewer
 * `useMyReactions` / `useMyUpvotes`, and `useAvailableRankers` for the sorts
 * `useCommentTree` can ask for); every write goes through one of the
 * exported `...Async` functions (`postOrUpdateCommentAsync`,
 * `confirmCommentAsync`, `deleteCommentAsync`, `pinCommentAsync`,
 * `unpinCommentAsync`, and the reaction/upvote setters). Callers outside this
 * file should never reach for `datastore.getApiClient().comments.*` directly —
 * keeping cache consistency (seeding, optimistic patches, invalidation)
 * centralized here means a call site can't forget to do it correctly.
 *
 * The teaser-embed read and the featured-comment writes are `spaces.*`
 * endpoints, so they live in `./spaces.ts` (`useCommentTeaser`,
 * `featureCommentAsync`, `unfeatureCommentAsync`) rather than here.
 *
 * `useCommentTree`, `useMyTopLevelComment`, `useCommentStats`, and
 * `useCommentWithAncestors` are thin wrappers over oRPC endpoints;
 * `useCommentByKey` is a thin wrapper over the `comments.get` oRPC endpoint
 * (depth 0, ancestors unused) — shares its cache with `useCommentTree` (see the
 * seeding effect inside it).
 *
 * The write functions optimistically patch the cached tree where a stable
 * key already exists (an edit or a removal), rolling the patch back via
 * invalidation if the request fails; a brand-new post has no key to patch by
 * until the server mints one, so it only appears once the success-path
 * invalidation (also owned by the write function, not the caller) resolves.
 * See each function's own doc comment for its exact optimistic-update,
 * invalidation, and rollback behavior.
 */

import { applyReactionChange, applyUpvoteChange, type CommentInteractions } from '@repo/shared/comment-interactions';
import type { ReportReason } from '@repo/shared/community-guidelines';
import type {
    Comment,
    CommentResult,
    CommentStats,
    CommentTreeNode,
    CommentWithAncestors,
    JsonValue,
} from '@repo/shared/orpc/procedures/comments';
import { type CommentPerspectiveInput, perspectiveVoteLabel } from '@repo/shared/perspectives';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import type { CommentConfig } from 'component/comment';
import { useLanguage } from 'component/translation';
import { useEffect, useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore, useInstanceKey, usePersonaKey, useSessionData, useSiloKey } from 'util/datastore';
import { useConfig, useEnabledFeatures } from 'util/features-hooks';
import { fireAndForget } from 'util/hooks';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export type { CommentStats, CommentTreeNode, CommentWithAncestors } from '@repo/shared/orpc/procedures/comments';

import { invalidateParticipants } from './participants';
import { useSettings } from './settings';
import { instanceParamsOf, siloParamsOf } from './silo-params';
import { useInstanceQuery } from './use-silo-query';

/**
 * Flattens a comment tree's top-level nodes and every nested reply into one
 * array, tree order (top-level comments in ranked order, each followed
 * immediately by its own flattened replies). Used by admin/analysis tooling
 * that needs the full comment set rather than the rendered nesting.
 */
export function flattenCommentTree(tree: CommentTreeNode[]): Comment[] {
    return tree.flatMap((node) => [node.comment, ...flattenCommentTree(node.replies)]);
}

export type MyTopLevelComment = {
    comment: Comment | null;
    replyCount: number;
    pending: boolean;
};

/** The feature values a viewer's own answer adds to a stats breakdown. */
function withIncrement(bucket: Record<string, number>, key: string): Record<string, number> {
    return { ...bucket, [key]: (bucket[key] ?? 0) + 1 };
}

/**
 * Adds the viewer's own comment to the stats breakdowns while it waits for
 * moderation. Call it wherever a tally is rendered, passing the viewer's
 * comment from `useMyTopLevelComment`.
 *
 * `comments.stats` has no viewer, so it can only count what the space has
 * published. The author is the one exception who already sees their held
 * comment in the thread, tagged "under review", so a bare tally would show
 * them a poll their own answer is missing from. Patching it in at the call
 * site keeps the server aggregate public and honest, and costs one vote's
 * difference between what the author sees and what everyone else does.
 *
 * Only buckets the space already publishes get patched, so a feature that is
 * switched off stays absent. A rejected comment is never counted: the author
 * can see it was refused, and a refused view is not one the space heard.
 *
 * `counts.perspectiveRespondents` is the denominator the perspective
 * percentages divide by, so it moves with the tallies it explains: one held
 * comment adds one respondent, however many perspectives it selected.
 *
 * @public
 */
export function withMyPendingAnswer(stats: CommentStats | undefined, myComment: Comment | null | undefined): CommentStats | undefined {
    if (!stats || !myComment?.moderationState?.inReview) return stats;
    const breakdowns = { ...stats.breakdowns };
    const counts = { ...stats.counts };
    const slider = myComment.properties.slider;
    if (breakdowns.slider && typeof slider === 'number') {
        breakdowns.slider = withIncrement(breakdowns.slider, String(slider));
    }
    if (breakdowns.perspectives) {
        const myTags = myComment.perspectives ?? [];
        for (const tag of myTags) {
            breakdowns.perspectives = withIncrement(breakdowns.perspectives, perspectiveVoteLabel(tag));
        }
        // The respondent denominator counts people, so one held comment adds
        // one however many perspectives it carries.
        if (myTags.length > 0) {
            counts.perspectiveRespondents = (counts.perspectiveRespondents ?? 0) + 1;
        }
    }
    return { ...stats, breakdowns, counts };
}

/**
 * Instance-wide comment aggregates, computed server-side via the
 * `comments.stats` oRPC procedure. `stats` is undefined until the first fetch
 * resolves and `isPending` is true while loading, so callers choose how to
 * handle loading (skeleton, hide, or treat as empty) rather than the hook
 * fabricating zero-filled data that can't be told apart from a genuinely
 * empty space.
 *
 * These are the space's public numbers, so they exclude anything held for
 * moderation. A viewer rendering a tally that should include their own held
 * answer passes this through `withMyPendingAnswer`.
 *
 * @public
 */
export function useCommentStats(): { stats: CommentStats | undefined; isPending: boolean } {
    const utils = useApiQueryUtils();
    const { data, isPending } = useInstanceQuery({ procedure: utils.comments.stats });
    return { stats: data, isPending };
}

/**
 * Invalidate the cached comment stats for every space so the next read
 * refetches. Called by `invalidateCommentDerivedData` after a comment write;
 * intentionally broad (all spaces, not just the current one) because call
 * sites don't all thread a specific `spaceKey` through.
 *
 * @public
 */
export function invalidateCommentStats(datastore: Datastore) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.comments.stats.key(), refetchType: 'all' }));
}

/**
 * Fetches a single comment by key. Intended for cases where you genuinely
 * only need information about one specific comment — most notably the
 * Composer, where pulling a whole `CommentTree` makes no sense. Must not be
 * called inside a loop or once-per-rendered-comment: that would be O(n) hooks
 * and bypass the tree's pagination.
 *
 * @public
 */
export function useCommentByKey(key: string | null): Comment | null {
    const utils = useApiQueryUtils();
    const { data } = useInstanceQuery({
        procedure: utils.comments.get,
        input: { commentKey: key ?? '', depth: 0 },
        enabled: !!key,
    });
    return data ? data.comment : null;
}

/**
 * The viewer's own top-level comment (their "answer") and the count of its
 * visible replies, read from the `comments.myTopLevel` oRPC endpoint. Returns
 * `pending: true` while the read is in flight — consumers that decide whether
 * to edit-vs-create an answer MUST wait for `pending: false` rather than treat
 * a not-yet-loaded read as "no comment". Includes text-less slider votes.
 *
 * With `fresh`, it reads again on mount and stays `pending` until that read
 * lands. The cached answer can predate a login that didn't reload the page,
 * so a caller about to write on the strength of it asks for a fresh one.
 * Later background refetches don't make it `pending` again, so a caller that
 * swaps its content for a spinner while pending doesn't lose what it holds.
 *
 * @public
 */
export function useMyTopLevelComment({ fresh = false }: { fresh?: boolean } = {}): MyTopLevelComment {
    const utils = useApiQueryUtils();
    const { data, isLoading, isFetching, isFetchedAfterMount } = useInstanceQuery({
        procedure: utils.comments.myTopLevel,
        options: fresh ? { refetchOnMount: 'always' } : undefined,
    });
    const awaitingFreshRead = fresh && isFetching && !isFetchedAfterMount;
    return { comment: data?.comment ?? null, replyCount: data?.replyCount ?? 0, pending: isLoading || awaitingFreshRead };
}

/**
 * The viewer's own top-level comment, read from the server rather than the
 * cache. The cached answer can predate a login that didn't reload the page,
 * so code about to write an answer on the strength of it reads this instead.
 */
export async function fetchMyTopLevelCommentAsync(datastore: Datastore): Promise<Comment | null> {
    const utils = datastore.getApiQueryUtils();
    const data = await datastore.getQueryClient().fetchQuery({
        ...utils.comments.myTopLevel.queryOptions({ input: instanceParamsOf(datastore) }),
        staleTime: 0,
    });
    return data.comment ?? null;
}

/**
 * Invalidate the cached "my top-level comment" read so the next render
 * refetches. Called by `invalidateCommentDerivedData` after a comment write
 * that could change the viewer's top-level comment or its replies.
 *
 * @public
 */
export function invalidateMyTopLevelComment(datastore: Datastore) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.comments.myTopLevel.key(), refetchType: 'all' }));
}

/**
 * Walks a `comments.list` wire tree and returns, for every comment key in it,
 * the comment itself plus its ancestor chain (root → parent) — exactly what
 * `comments.get({ commentKey, depth: 0 })` would have computed for that key.
 * Used to seed `comments.get`'s cache from an already-fetched tree.
 */
function collectCommentAncestorsByKey(
    nodes: CommentTreeNode[],
    ancestors: Comment[] = [],
): Map<string, { comment: Comment; ancestors: Comment[] }> {
    const byKey = new Map<string, { comment: Comment; ancestors: Comment[] }>();
    for (const node of nodes) {
        byKey.set(node.comment.commentKey, { comment: node.comment, ancestors });
        for (const [key, entry] of collectCommentAncestorsByKey(node.replies, [...ancestors, node.comment])) {
            byKey.set(key, entry);
        }
    }
    return byKey;
}

const DEFAULT_RANKING_MODE = 'most-recent';

// The contract's `cursorParams` cap — high enough to never truncate a thread.
export const ALL_COMMENTS_LIMIT = 100000;

const COMMENT_EXPANSIONS_PATH = ['comments', 'expansions'];

/** A sort the viewer can pick; `key` is the `sort` sent to `comments.list`. */
export type CommentRanker = { key: string; label: TLabel };

const BEST_RANKER: CommentRanker = { key: 'best', label: t('llm_ranking.best', 'Best') };

const STANDARD_RANKERS: CommentRanker[] = [
    { key: 'most-recent', label: t('simple_ranking.newest', 'Newest') },
    { key: 'most-reactions', label: t('simple_ranking.most_reactions', 'Most Reactions') },
];

const AUDIENCE_INPUT_RANKERS: CommentRanker[] = [
    { key: 'most-popular', label: t('most_popular.most_popular_label', 'Most popular') },
    { key: 'most-recent', label: t('most_popular.most_recent_label', 'Most recent') },
];

/**
 * The sorts the current space offers, in menu order; the first is the default.
 *
 * - Audience Input collects upvotes, so it sorts by popularity where other
 *   formats sort by reactions.
 * - "Best" leads when the silo has prosocial ranking on and the space has
 *   `llm-ranking` on; the server only scores comments in spaces with that
 *   flag.
 */
export function useAvailableRankers(): CommentRanker[] {
    const features = useEnabledFeatures();
    // Unloaded settings count as allowing Best, so the default sort doesn't
    // flip from Newest to Best, refetching the tree, once they arrive.
    const siloAllowsBest = useSettings()?.prosocialRankingEnabled ?? true;
    const formatRankers = features.audience_input ? AUDIENCE_INPUT_RANKERS : STANDARD_RANKERS;
    return siloAllowsBest && features['llm-ranking'] ? [BEST_RANKER, ...formatRankers] : formatRankers;
}

/**
 * How many top-level comments to fetch, doubling with each
 * `expandCommentLimit`. Null when the config sets no `commentPageSize`,
 * meaning fetch the whole thread.
 *
 * @public
 */
export function useCommentLimit(): number | null {
    const { commentPageSize } = useConfig<CommentConfig>();
    const expansions = (useSessionData(COMMENT_EXPANSIONS_PATH) as number | null) ?? 0;
    if (commentPageSize == null) return null;
    return Math.min(commentPageSize * 2 ** expansions, ALL_COMMENTS_LIMIT);
}

/** @public */
export function expandCommentLimit(datastore: Datastore) {
    const expansions = (datastore.getSessionData(COMMENT_EXPANSIONS_PATH) as number | null) ?? 0;
    datastore.setSessionData(COMMENT_EXPANSIONS_PATH, expansions + 1);
}

/**
 * Ranked comment tree for the current space — top-level comments with replies
 * nested by the server. `sort` is the user's session sort selection, falling
 * back to the space's default ranker (the one the sort menu shows first).
 *
 * Fetches `useCommentLimit()` top-level comments, each with its whole reply
 * subtree. Callers can override the limit and sort for their own reads.
 *
 * `isPending` is true until the first fetch resolves, same escape hatch as
 * `useCommentStats()`: a fresh space and a genuinely empty space both start
 * with `tree: []`, so callers that show an empty-state need `isPending` to
 * tell them apart and avoid flashing that empty state before real comments
 * arrive.
 *
 * @public
 */
export function useCommentTree(query?: { limit?: number; sort?: string }): { tree: CommentTreeNode[]; isPending: boolean } {
    const utils = useApiQueryUtils();
    const queryClient = useQueryClient();
    const siloKey = useSiloKey();
    const spaceKey = useInstanceKey();
    const locale = useLanguage();
    const [defaultRanker] = useAvailableRankers();
    const sessionSort = (useSessionData(['ranking', 'sortMode']) as string | null) ?? defaultRanker.key;
    const configuredLimit = useCommentLimit();
    const limit = query?.limit ?? configuredLimit ?? ALL_COMMENTS_LIMIT;
    const sort = query?.sort ?? sessionSort;
    const { data, isPending } = useInstanceQuery({
        procedure: utils.comments.list,
        input: { sort, limit },
        // Without this, a raised limit re-keys the query and empties the feed.
        options: { placeholderData: keepPreviousData },
    });
    const nodes = useMemo(() => data?.items ?? [], [data]);

    // Seed `comments.get`'s cache (depth: 0 — the shape `useCommentByKey` and
    // `useCommentWithAncestors` both request) for every comment already loaded
    // here, so looking up one of them individually is a cache hit instead of a
    // second full server-side recompute.
    useEffect(() => {
        const entriesByKey = collectCommentAncestorsByKey(nodes);
        for (const [commentKey, { comment, ancestors }] of entriesByKey) {
            const seeded: CommentWithAncestors = { comment, ancestors, replies: [], nextCursor: null };
            const key = utils.comments.get.key({ type: 'query', input: { siloKey, locale, spaceKey, commentKey, depth: 0 } });
            queryClient.setQueryData(key, seeded);
        }
    }, [nodes, queryClient, utils, siloKey, locale, spaceKey]);

    return { tree: nodes, isPending };
}

/**
 * One-shot imperative fetch of every visible comment in the current space,
 * flattened — for non-component async callbacks that can't call
 * `useCommentTree()` (a hook). Prefer `useCommentTree()` inside components.
 */
export async function fetchAllCommentsAsync(datastore: Datastore): Promise<Comment[]> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    const { items } = await datastore.getApiClient().comments.list({
        siloKey,
        locale,
        spaceKey,
        sort: DEFAULT_RANKING_MODE,
        limit: ALL_COMMENTS_LIMIT,
    });
    return flattenCommentTree(items);
}

/**
 * Seed `comments.get`'s cache with a comment record straight from a
 * create/update/confirm write response — the imperative counterpart to
 * `useCommentTree`'s seeding effect above. Without this, a comment read via
 * `useCommentByKey` right after posting it stays "not found" until the
 * `invalidateCommentDerivedData` refetch resolves, which flashes the composer
 * back into "new comment" mode for a moment (and, in tests whose API mock
 * doesn't simulate a live backend, never resolves at all). Ancestors are
 * seeded empty — the caller only has the written record, not its parent
 * chain — so a `useCommentWithAncestors` read for this exact key within the
 * cache's `staleTime` may show no ancestors until the next real fetch.
 */
function seedCommentCache(datastore: Datastore, comment: Comment) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    const seeded: CommentWithAncestors = { comment, ancestors: [], replies: [], nextCursor: null };
    queryClient.setQueryData(
        utils.comments.get.key({ type: 'query', input: { siloKey, locale, spaceKey, commentKey: comment.commentKey, depth: 0 } }),
        seeded,
    );
}

/**
 * Seed `comments.myTopLevel`'s cache with a comment record straight from a
 * top-level create/update response, the same way `seedCommentCache` seeds
 * `comments.get`. Without this, the "my answer" card relies solely on
 * `invalidateMyTopLevelComment`'s fire-and-forget refetch, which shares a
 * query key with any still-in-flight refetch from a previous write (e.g. the
 * create that immediately precedes an edit) — React Query dedupes onto that
 * older in-flight request rather than starting a new one, so the cache can
 * end up seeded with the pre-edit comment even though the edit already
 * succeeded. Seeding synchronously with the write's own authoritative result
 * closes that race the same way it's already closed for `comments.get`.
 */
function seedMyTopLevelCommentCache(datastore: Datastore, comment: Comment) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    const key = utils.comments.myTopLevel.key({ type: 'query', input: { siloKey, locale, spaceKey } });
    const previous = queryClient.getQueryData<{ comment: Comment | null; replyCount: number }>(key);
    queryClient.setQueryData(key, { comment, replyCount: previous?.replyCount ?? 0 });
}

/**
 * Recursively applies `updater` to the wire comment matching `commentKey`
 * anywhere in a tree (top-level or nested reply), returning a new tree with
 * the change applied immutably. `updater` returning `null` removes that node
 * — and, since the recursion never reaches its replies, its whole subtree —
 * matching how a genuinely hidden/deleted comment disappears from the
 * server's own tree (`comments.list` drops a hidden node whole).
 */
function updateCommentTree(nodes: CommentTreeNode[], commentKey: string, updater: (comment: Comment) => Comment | null): CommentTreeNode[] {
    const result: CommentTreeNode[] = [];
    for (const node of nodes) {
        if (node.comment.commentKey === commentKey) {
            const updated = updater(node.comment);
            if (updated) result.push({ ...node, comment: updated });
            continue;
        }
        result.push({ ...node, replies: updateCommentTree(node.replies, commentKey, updater) });
    }
    return result;
}

type CachedCommentList = { items: CommentTreeNode[]; nextCursor: string | null };

/**
 * Optimistically patches every cached `comments.list` tree (there can be more
 * than one, keyed by different `sort` and `limit` values) by applying
 * `updater` to the node matching `commentKey`. Used so a pending edit or
 * removal shows in the rendered tree immediately, without waiting for the
 * server round trip.
 */
function patchCachedCommentTrees(datastore: Datastore, commentKey: string, updater: (comment: Comment) => Comment | null) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    queryClient.setQueriesData<CachedCommentList>({ queryKey: utils.comments.list.key() }, (data) => {
        if (!data) return data;
        return { ...data, items: updateCommentTree(data.items, commentKey, updater) };
    });
}

/**
 * Create or update a comment via oRPC. A `commentKey` edits that comment
 * (`comments.update`); without one, it posts a new comment
 * (`comments.create`) and the server mints its key.
 *
 * An edit is optimistically reflected in every cached tree immediately — the
 * key is already known, so the change can be located and patched before the
 * request resolves. A new post isn't optimistically inserted: there's no key
 * to patch by until the server mints one, and guessing its rank position in
 * an arbitrary sort order isn't worth the complexity — it appears once the
 * derived-data invalidation below resolves.
 *
 * On success, `comments.get`'s cache is seeded with the authoritative
 * response (and `comments.myTopLevel`'s too, for a top-level write) and every
 * comment-derived read (tree/stats/myTopLevel/participants) is invalidated
 * automatically — the caller doesn't have to remember to. On failure, an
 * edit's optimistic patch is rolled back by
 * invalidating just the tree (forcing a refetch back to the last real server
 * state) before the error is rethrown. A rejected edit is rolled back the
 * same way — the server leaves the live comment's text unchanged on a
 * rejection.
 */
export async function postOrUpdateCommentAsync(
    datastore: Datastore,
    input: {
        commentKey?: string;
        text: string;
        parentKey?: string | null;
        properties?: Record<string, JsonValue>;
        perspectives?: CommentPerspectiveInput[];
    },
): Promise<CommentResult> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    const isEdit = !!input.commentKey;
    const commentKey = input.commentKey;

    if (isEdit && commentKey) {
        // Perspectives are deliberately not patched: the input carries the
        // write shape (bare preset keys) while the cache holds the read shape,
        // so the server's resolved tags come back with the response instead.
        patchCachedCommentTrees(datastore, commentKey, (comment) => ({
            ...comment,
            text: input.text,
            properties: { ...comment.properties, ...input.properties },
        }));
    }

    let result: CommentResult;
    try {
        result =
            isEdit && commentKey
                ? await datastore.getApiClient().comments.update({
                      siloKey,
                      locale,
                      spaceKey,
                      commentKey,
                      text: input.text,
                      properties: input.properties,
                      perspectives: input.perspectives,
                  })
                : await datastore.getApiClient().comments.create({
                      siloKey,
                      locale,
                      spaceKey,
                      text: input.text,
                      parentKey: input.parentKey ?? null,
                      properties: input.properties,
                      perspectives: input.perspectives,
                  });
    } catch (error) {
        if (isEdit) invalidateCommentTree(datastore);
        throw error;
    }

    if (result.outcome === 'posted') {
        seedCommentCache(datastore, result.comment);
        if (!input.parentKey) seedMyTopLevelCommentCache(datastore, result.comment);
        invalidateCommentDerivedData(datastore);
    } else if (isEdit) {
        invalidateCommentTree(datastore);
    }
    return result;
}

/**
 * Confirm a comment past an initial moderation rejection — "post anyway" for
 * a minor violation, an appeal, or an admin's automatic bypass. The comment
 * isn't visible in any cached tree before this resolves (it was held back by
 * moderation), so there's nothing to optimistically patch; on success,
 * `comments.get`'s cache is seeded with the result and every comment-derived
 * read is invalidated, exactly as a fresh post would be — the comment is now
 * live and visible for the first time.
 */
export async function confirmCommentAsync(datastore: Datastore, input: { commentKey: string; appealText?: string }): Promise<Comment> {
    const result = await datastore.getApiClient().comments.confirm({
        ...siloParamsOf(datastore),
        spaceKey: datastore.getInstanceKey(),
        commentKey: input.commentKey,
        appealText: input.appealText,
    });
    seedCommentCache(datastore, result);
    invalidateCommentDerivedData(datastore);
    return result;
}

/**
 * Delete a comment via `comments.remove` (a server-side soft delete).
 *
 * The server owns the outcome, not the client: a deleted top-level post keeps
 * showing as a tombstone when an admin views it or it still has a visible
 * reply, while a reply-less post and every deleted reply are dropped whole. The
 * client can't know which up front, so it doesn't guess — it optimistically
 * patches the node in place as the caller's "deletion in progress" tombstone
 * (`pendingTombstone`, which keeps the node's replies), then the
 * comment-derived invalidation below refetches the tree and the node settles to
 * its final tombstone or disappears. Invalidating only after the request
 * resolves keeps the refetch from racing the delete and rebuilding derived
 * views from stale data. On failure the optimistic patch is rolled back by
 * refetching the tree before the error is rethrown so the caller can show it.
 *
 * @public
 */
export async function deleteCommentAsync(datastore: Datastore, pendingTombstone: Comment): Promise<void> {
    patchCachedCommentTrees(datastore, pendingTombstone.commentKey, () => pendingTombstone);
    try {
        await datastore.getApiClient().comments.remove({
            ...siloParamsOf(datastore),
            spaceKey: datastore.getInstanceKey(),
            commentKey: pendingTombstone.commentKey,
        });
    } catch (error) {
        invalidateCommentTree(datastore);
        throw error;
    }
    invalidateCommentDerivedData(datastore);
}

// ---------------------------------------------------------------------------
// Reactions / upvotes
//
// The comment tree carries only the shared reaction/upvote *tallies*
// (`reactionCounts`, `upvoteCount`) — identical for every viewer. The viewer's
// own reaction/upvote state is per-viewer and read separately via
// `comments.reactionsMine` / `comments.upvotesMine`, joined to the tree by
// comment key. A write patches both halves: the viewer's own state in the mine
// cache, and the shared tally on every cached tree.
// ---------------------------------------------------------------------------

const EMPTY_MY_REACTIONS: Record<string, string> = {};

/**
 * The viewer's own reaction on each comment in the current space, as a
 * `{ [commentKey]: reactionType }` map — comments the viewer hasn't reacted to
 * are absent. Backed by `comments.reactionsMine`. Empty for logged-out
 * viewers, who don't fire the authenticated read.
 *
 * @public
 */
export function useMyReactions(): Record<string, string> {
    const spaceKey = useInstanceKey();
    const personaKey = usePersonaKey();
    const utils = useApiQueryUtils();
    const { data } = useInstanceQuery({ procedure: utils.comments.reactionsMine, enabled: !!spaceKey && !!personaKey });
    return data ?? EMPTY_MY_REACTIONS;
}

/**
 * The set of comment keys the viewer has upvoted in the current space. Backed
 * by `comments.upvotesMine`. Empty for logged-out viewers, who don't fire the
 * authenticated read.
 *
 * @public
 */
export function useMyUpvotes(): ReadonlySet<string> {
    const spaceKey = useInstanceKey();
    const personaKey = usePersonaKey();
    const utils = useApiQueryUtils();
    const { data } = useInstanceQuery({ procedure: utils.comments.upvotesMine, enabled: !!spaceKey && !!personaKey });
    return useMemo(() => new Set(data ?? []), [data]);
}

// Builds a `CommentInteractions` for a wire comment from its shared tallies
// plus the viewer's own pre-mutation state, so the shared delta math
// (`applyReactionChange` / `applyUpvoteChange`) can compute the new tally to
// patch onto the tree — keeping that math in one place with the server.
function interactionsOf(comment: Comment, myReactionType: string | null, myUpvoted: boolean): CommentInteractions {
    return { reactionCounts: comment.reactionCounts, myReactionType, upvoteCount: comment.upvoteCount, myUpvoted };
}

/**
 * Optimistically applies a reaction change (`nextReactionType` null clears it):
 * patch the viewer's own state in the `reactionsMine` cache and the shared
 * `reactionCounts` tally on every cached tree, call the endpoint, then
 * overwrite the comment node with the authoritative response tally on success
 * — no invalidation needed, since the mine cache already holds the action the
 * viewer just took. On failure, roll the mine cache back and invalidate the
 * tree to restore the tally.
 */
async function writeMyReactionAsync(datastore: Datastore, commentKey: string, nextReactionType: string | null): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const mineKey = utils.comments.reactionsMine.queryOptions({ input: { siloKey, locale, spaceKey } }).queryKey;

    const previousMine = queryClient.getQueryData<Record<string, string>>(mineKey) ?? {};
    const previousReactionType = previousMine[commentKey] ?? null;

    const nextMine = { ...previousMine };
    if (nextReactionType) nextMine[commentKey] = nextReactionType;
    else delete nextMine[commentKey];
    queryClient.setQueryData(mineKey, nextMine);

    patchCachedCommentTrees(datastore, commentKey, (comment) => ({
        ...comment,
        reactionCounts: applyReactionChange(interactionsOf(comment, previousReactionType, false), {
            from: previousReactionType,
            to: nextReactionType,
        }).reactionCounts,
    }));

    try {
        const updated =
            nextReactionType != null
                ? await datastore
                      .getApiClient()
                      .comments.setReaction({ siloKey, locale, spaceKey, commentKey, reactionType: nextReactionType })
                : await datastore.getApiClient().comments.removeReaction({ siloKey, locale, spaceKey, commentKey });
        seedCommentCache(datastore, updated);
        patchCachedCommentTrees(datastore, commentKey, () => updated);
    } catch (error) {
        queryClient.setQueryData(mineKey, previousMine);
        invalidateCommentTree(datastore);
        throw error;
    }
}

/**
 * Optimistically toggles the viewer's upvote: patch the viewer's own state in
 * the `upvotesMine` cache and the shared `upvoteCount` tally on every cached
 * tree, call the endpoint, then overwrite the comment node with the
 * authoritative response tally on success. On failure, roll the mine cache
 * back and invalidate the tree.
 */
async function writeMyUpvoteAsync(datastore: Datastore, commentKey: string, nextUpvoted: boolean): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const mineKey = utils.comments.upvotesMine.queryOptions({ input: { siloKey, locale, spaceKey } }).queryKey;

    const previousUpvotes = queryClient.getQueryData<string[]>(mineKey) ?? [];
    const wasUpvoted = previousUpvotes.includes(commentKey);

    const nextUpvotes = nextUpvoted
        ? wasUpvoted
            ? previousUpvotes
            : [...previousUpvotes, commentKey]
        : previousUpvotes.filter((key) => key !== commentKey);
    queryClient.setQueryData(mineKey, nextUpvotes);

    patchCachedCommentTrees(datastore, commentKey, (comment) => ({
        ...comment,
        upvoteCount: applyUpvoteChange(interactionsOf(comment, null, wasUpvoted), nextUpvoted).upvoteCount,
    }));

    try {
        const updated = nextUpvoted
            ? await datastore.getApiClient().comments.setUpvote({ siloKey, locale, spaceKey, commentKey })
            : await datastore.getApiClient().comments.removeUpvote({ siloKey, locale, spaceKey, commentKey });
        seedCommentCache(datastore, updated);
        patchCachedCommentTrees(datastore, commentKey, () => updated);
    } catch (error) {
        queryClient.setQueryData(mineKey, previousUpvotes);
        invalidateCommentTree(datastore);
        throw error;
    }
}

/**
 * Set (create or swap) the viewer's reaction on a comment.
 *
 * @public
 */
export async function setReactionAsync(datastore: Datastore, input: { commentKey: string; reactionType: string }): Promise<void> {
    await writeMyReactionAsync(datastore, input.commentKey, input.reactionType);
}

/**
 * Remove the viewer's reaction from a comment.
 *
 * @public
 */
export async function removeReactionAsync(datastore: Datastore, input: { commentKey: string }): Promise<void> {
    await writeMyReactionAsync(datastore, input.commentKey, null);
}

/**
 * Set the viewer's upvote on a comment (idempotent).
 *
 * @public
 */
export async function setUpvoteAsync(datastore: Datastore, input: { commentKey: string }): Promise<void> {
    await writeMyUpvoteAsync(datastore, input.commentKey, true);
}

/**
 * Remove the viewer's upvote from a comment (idempotent).
 *
 * @public
 */
export async function removeUpvoteAsync(datastore: Datastore, input: { commentKey: string }): Promise<void> {
    await writeMyUpvoteAsync(datastore, input.commentKey, false);
}

/**
 * Pin a comment to the top of the feed, or reposition it among already-pinned
 * siblings — `position` is the target index within the pinned group, omitted
 * to append (see `comments.pin`). Invalidates the tree on success.
 */
export async function pinCommentAsync(
    datastore: Datastore,
    { commentKey, position }: { commentKey: string; position?: number },
): Promise<Comment> {
    const result = await datastore.getApiClient().comments.pin({
        ...siloParamsOf(datastore),
        spaceKey: datastore.getInstanceKey(),
        commentKey,
        position,
    });
    invalidateCommentTree(datastore);
    return result;
}

/**
 * Unpin a comment from the feed. Invalidates the tree on success.
 */
export async function unpinCommentAsync(datastore: Datastore, commentKey: string): Promise<Comment> {
    const result = await datastore.getApiClient().comments.unpin({
        ...siloParamsOf(datastore),
        spaceKey: datastore.getInstanceKey(),
        commentKey,
    });
    invalidateCommentTree(datastore);
    return result;
}

/**
 * The target comment, its ancestor chain (root → parent), and a depth-limited
 * page of its replies — backed by the `comments.get` oRPC endpoint. The zdf
 * jump-to-comment widget consumes the
 * ancestor chain to expand the path to a linked reply; it requests `depth: 0`
 * (no replies) because the comment itself is rendered by `useCommentTree`.
 * Returns `null` while loading, disabled, or on error (e.g. the linked comment
 * isn't visible), so callers no-op safely.
 *
 * @public
 */
export function useCommentWithAncestors(commentKey: string | null): CommentWithAncestors | null {
    const utils = useApiQueryUtils();
    // `sort` is omitted: it only orders replies, and `depth: 0` returns none.
    const { data: commentWithAncestors } = useInstanceQuery({
        procedure: utils.comments.get,
        input: { commentKey: commentKey ?? '', depth: 0 },
        enabled: !!commentKey,
    });
    return useMemo(() => commentWithAncestors ?? null, [commentWithAncestors]);
}

/**
 * Invalidate the server comment tree after a local write that affects it —
 * called internally by this file's write functions, and directly by other
 * features whose own writes change what the tree renders without changing
 * comment data itself (a reaction, upvote, or pin). Uses the per-procedure
 * prefix key (no `input`) because the list is an infinite query whose key
 * carries sort/limit, so an exact key never matches. `refetchType: 'all'` so
 * cached-but-inactive queries refetch too.
 *
 * @public
 */
export function invalidateCommentTree(datastore: Datastore) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.comments.list.key(), refetchType: 'all' }));
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.comments.get.key(), refetchType: 'all' }));
}

/**
 * Invalidate every read derived from a space's comments — called internally
 * by this file's write functions on a successful post/edit/delete/confirm. A
 * comment write can change the ranked tree, the instance-wide stats, the
 * viewer's own-answer read, and the participant list, so it fans out to all
 * of them. Narrower writes that touch only the tree — a reaction, upvote, or
 * pin — call `invalidateCommentTree` directly instead.
 *
 * @public
 */
export function invalidateCommentDerivedData(datastore: Datastore) {
    invalidateCommentStats(datastore);
    invalidateCommentTree(datastore);
    invalidateMyTopLevelComment(datastore);
    invalidateParticipants(datastore);
}

/**
 * Report a comment.
 */
export async function reportCommentAsync(datastore: Datastore, commentKey: string, violations: ReportReason[]): Promise<void> {
    await datastore.getApiClient().comments.report({
        ...siloParamsOf(datastore),
        spaceKey: datastore.getInstanceKey(),
        commentKey,
        violations,
    });
}
